import { insightsIntl } from "../strings/intl";
import type { Theme } from "@wayflyer/flyui";
import { type IntlShape, useIntl } from "react-intl";
import { DateRangeOptions, Icon, Text, Tooltip, useTheme } from "@wayflyer/flyui";
import { IconArrowRight16Line, IconHelpCircle16Line } from "@wayflyer/flyui-icons/16/line";
import React from "react";

import { MetricTypes, getMetricOverrideFromMetricType } from "../autogen/metrics";
import { metricDescription, metricTooltip } from "../strings/metric-messages";
import { getReferenceData } from "../overview/overview-datapoints-2";
import { formatValue } from "../strings/type-formatting";
import { yoyReferenceForTimeRange } from "../utils/custom-time-range-helpers";
import type { DashboardOutput } from "../utils/fetch-api";
import { getDashboardOutput } from "../utils/fetch-api";
import type { ReferenceData } from "../utils/metrics";
import { includeMetricDecimal } from "../utils/metrics";
import type { CustomTimeRange, MetricType } from "../utils/types";

import { SOURCE_METRICS } from "./constants";
import { Logo } from "./logo";
import { getChannelMetric, getSummaryData } from "./metrics";
import { PAID_CHANNELS_SUMMARY, PaidChannel, paidChannelStrings } from "./paid-channels";
import { ReferencePeriod, comparisonForReferencePeriod, makePreviousPeriod } from "./reference";
import { Container, FlexContainer, LeftFlexContainer, LogoContainer, TileTooltipContainer } from "./ui/styled";
import type { AxisConfig, GroupedTilesProps, Tile } from "./ui/tiles";
import type { TabProps } from "./paid-channels-summary-screen";

const getInitialGroupedTilesProps = () => {
  const rows = {
    elements: [],
  };
  const columns = {
    elements: [],
  };
  return {
    tiles: {},
    columns: columns,
    rows: rows,
  };
};

interface TileTooltipProps {
  text: string;
}

const TileTooltip = ({ text }: TileTooltipProps) => {
  return (
    <Tooltip content={text}>
      <TileTooltipContainer>
        <IconHelpCircle16Line />
      </TileTooltipContainer>
    </Tooltip>
  );
};

const augmentTileWithReferenceData = (
  tile: Tile,
  referenceData: ReferenceData | null,
  referencePeriod: ReferencePeriod
): Tile => {
  if (referenceData) {
    tile["metricReferenceSuccess"] = referenceData.metricReferenceSuccess;
    tile["metricReferenceDirection"] = referenceData.metricReferenceDirection;
    tile["metricReferenceValue"] = referenceData.metricReferenceValue;
    tile["metricReferenceText"] = comparisonForReferencePeriod(referencePeriod);
  }
  return tile;
};

const makeTile = (
  channel: PaidChannel | typeof PAID_CHANNELS_SUMMARY,
  metric: MetricTypes,
  metricValue: number | null,
  metricData: MetricType,
  isFirstSortedRow: boolean,
  isFirstSortedColumn: boolean,
  theme: Theme,
  intl: IntlShape
): Tile => {
  const isSummary = channel === PAID_CHANNELS_SUMMARY;
  const tooltipText = metricTooltip(intl, metric);
  return {
    title: (
      <Container>
        <FlexContainer>
          {isSummary ? (
            <Text fontWeight="semiBold">
              {insightsIntl.formatMessage({ id: "PAID_CHANNELS_SUMMARY", defaultMessage: "Summary" })}
            </Text>
          ) : (
            <LogoContainer>
              <Logo name={channel as PaidChannel} />
              <Text>{paidChannelStrings[channel]?.description || ""}</Text>
            </LogoContainer>
          )}
          {isFirstSortedRow && !isFirstSortedColumn ? (
            <Icon color={theme.palette.content.accent.strong}>
              <IconArrowRight16Line />
            </Icon>
          ) : null}
        </FlexContainer>
        <LeftFlexContainer>
          <Text fontWeight={isFirstSortedColumn ? "semiBold" : "regular"}>
            {metricDescription(insightsIntl, metric)}
          </Text>
          {isFirstSortedColumn && tooltipText !== undefined ? <TileTooltip text={tooltipText} /> : null}
        </LeftFlexContainer>
      </Container>
    ),
    sortValue: metricValue,
    metricValue:
      metricValue == null
        ? null
        : formatValue({
            value: metricValue.toString(),
            metricType: metricData as MetricType,
            withDecimal: includeMetricDecimal(metric as MetricTypes),
            hackPercentage: true,
          }),
    style: {
      borderStyle: "solid",
      borderWidth: "2px",
      borderColor: isFirstSortedRow
        ? theme.palette.content.accent.strong
        : isFirstSortedColumn
        ? theme.palette.content.status.positive
        : theme.palette.bg.neutral.base,
    },
  };
};

const channelColumnLabel = (channel: PaidChannel): string => {
  switch (channel) {
    case PaidChannel.META_ADS:
      return insightsIntl.formatMessage({ id: "META_ADS", defaultMessage: "Meta Ads" });
    case PaidChannel.GOOGLE_ADS:
      return insightsIntl.formatMessage({ id: "GOOGLE_ADS", defaultMessage: "Google Ads" });
  }
};

const makeInitialColumns = () => {
  return [
    {
      id: PAID_CHANNELS_SUMMARY,
      text: insightsIntl.formatMessage({ id: "PAID_CHANNELS_SUMMARY", defaultMessage: "Summary" }),
      enabled: true,
    },
  ].concat(
    Object.keys(PaidChannel).map((channel) => {
      return {
        id: channel,
        text: channelColumnLabel(channel as PaidChannel),
        enabled: true,
      };
    })
  );
};

export const useGroupedTiles = (
  selectedTimeRange: CustomTimeRange,
  dashboardDataPath: string,
  currentPreset: DateRangeOptions,
  onError,
  referencePeriod
) => {
  const intl = useIntl();
  const theme = useTheme();
  const [needsUpdate, setNeedsUpdate] = React.useState<boolean>(true);
  const [requiresDataComputation, setRequiresDataComputation] = React.useState(false);

  const [props, setProps] = React.useState<GroupedTilesProps>(getInitialGroupedTilesProps());

  const [currentData, setCurrentData] = React.useState<DashboardOutput | null>(null);
  const [referenceData, setReferenceData] = React.useState<DashboardOutput | null>(null);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setNeedsUpdate(true);
  }, [currentPreset, selectedTimeRange, referencePeriod]);

  const fetch = (timeRange, cb) => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: SOURCE_METRICS.map((metric) => getChannelMetric(metric, PaidChannel.GOOGLE_ADS)),
          filename: "google_insights_ctr",
        },
        {
          metrics: SOURCE_METRICS.map((metric) => getChannelMetric(metric, PaidChannel.META_ADS)),
          filename: "meta_insights_overview_ctr",
        },
      ],
      timeRange,
      (data) => {
        cb(data);
      },
      null,
      onError
    );
  };

  if (needsUpdate) {
    const referenceTimeRange = (() => {
      if (referencePeriod === ReferencePeriod.PREVIOUS_YEAR) {
        return yoyReferenceForTimeRange(selectedTimeRange, currentPreset);
      } else {
        return makePreviousPeriod(selectedTimeRange);
      }
    })();

    setCurrentData(null);
    setReferenceData(null);
    fetch(selectedTimeRange, setCurrentData);
    fetch(referenceTimeRange, setReferenceData);
    setRequiresDataComputation(true);
    setNeedsUpdate(false);
  }

  const metrics = SOURCE_METRICS.concat([MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND]);

  const rowElements = React.useRef(
    metrics.map((key) => {
      return {
        id: key,
        text: metricDescription(insightsIntl, key),
        enabled: true,
      };
    })
  );

  const columnElements = React.useRef(makeInitialColumns());

  const onColumnEnable = React.useCallback(
    (id, element) => {
      columnElements.current = columnElements.current.map((elem) => {
        return {
          ...elem,
          enabled: element === elem.id ? id : elem.enabled,
        };
      });
      setRequiresDataComputation(true);
    },
    [columnElements]
  );

  const [rowSort, setRowSort] = React.useState(null);

  const SORT_ALPHABETICAL = "SORT_ALPHABETICAL";

  if (currentData && referenceData && requiresDataComputation) {
    const tiles = {};

    const summaryCurrent = getSummaryData(currentData.summary, metrics);
    const summaryReference = getSummaryData(referenceData.summary, metrics);

    // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    metrics.map((metric) => {
      const isFirstSortedRow = rowSort === metric;

      const metricData = getMetricOverrideFromMetricType(metric as MetricTypes, currentData.currencyCode);
      tiles[metric] = {};

      Object.keys(PaidChannel).map((channel: string) => {
        const isFirstSortedColumn = channel === columnElements.current[0].id;

        const channelMetric = getChannelMetric(metric, channel as PaidChannel);

        const metricValue = summaryCurrent[channelMetric] as number;
        const referenceValue = summaryReference[channelMetric] as number;
        const referenceData = getReferenceData(metricValue, referenceValue, metricData?.success_direction);

        const tile = makeTile(
          channel as PaidChannel,
          metric,
          metricValue,
          metricData as MetricType,
          isFirstSortedRow,
          isFirstSortedColumn,
          theme,
          intl
        );

        tiles[metric][channel] = augmentTileWithReferenceData(tile, referenceData, referencePeriod);
      });

      const metricValue = summaryCurrent[metric] as number;
      const referenceValue = summaryReference[metric] as number;
      const referenceData = getReferenceData(metricValue, referenceValue, metricData?.success_direction);

      const tile = makeTile(
        PAID_CHANNELS_SUMMARY,
        metric,
        metricValue,
        metricData as MetricType,
        isFirstSortedRow,
        PAID_CHANNELS_SUMMARY === columnElements.current[0].id,
        theme,
        intl
      );

      tiles[metric][PAID_CHANNELS_SUMMARY] = augmentTileWithReferenceData(tile, referenceData, referencePeriod);
    });

    const onRowSort = (elem) => {
      const newRows = (() => {
        if (elem.id === SORT_ALPHABETICAL) {
          return rowElements.current.sort((a, b) => (a.text > b.text ? 1 : -1));
        } else {
          const match = rowElements.current.filter((row) => row.id === elem.id);
          const unmatch = rowElements.current.filter((row) => row.id !== elem.id);
          return match.concat(unmatch);
        }
      })();

      const newColumns = (() => {
        if (elem.id === SORT_ALPHABETICAL) {
          return makeInitialColumns();
        } else {
          return columnElements.current.sort((a, b) => {
            if (a.id === PAID_CHANNELS_SUMMARY) {
              return -1;
            }
            if (b.id === PAID_CHANNELS_SUMMARY) {
              return 1;
            }
            return tiles[elem.id][a.id].sortValue < tiles[elem.id][b.id].sortValue ? 1 : -1;
          });
        }
      })();

      setRowSort(elem.id);
      rowElements.current = newRows;
      columnElements.current = newColumns;
      setRequiresDataComputation(true);
    };

    const rows: AxisConfig = {
      // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      elements: rowElements.current,
      sort: {
        name: "Sort",
        prefix: "Sort",
        initialValue: SORT_ALPHABETICAL,
        options: [
          {
            id: SORT_ALPHABETICAL,
            text: insightsIntl.formatMessage({ id: "SORT_ALPHABETICAL", defaultMessage: "Default A-Z" }),
          },
        ].concat(
          metrics.map((key) => {
            return {
              id: key,
              text: metricDescription(insightsIntl, key),
            };
          })
        ),
        onChange: onRowSort,
      },
    };

    const columns: AxisConfig = {
      select: {
        dropdownLabel: "Channel",
        onEnable: onColumnEnable,
      },
      // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      elements: columnElements.current,
    };

    const processed = {
      tiles: tiles,
      // eslint-disable-next-line react-hooks/refs -- Existing violation surfaced by oxlint's native React compiler rules; remove when fixed.
      columns: columns,
      // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      rows: rows,
    };

    // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setProps(processed);
    setRequiresDataComputation(false);
  }

  return props;
};

export const addGroupedTiles = (groupedTiles: GroupedTilesProps, tabData: TabProps[]): TabProps[] => {
  return tabData.concat({
    title: "Overview",
    groupedTiles,
  });
};
